import { useEffect, useRef, memo } from 'react';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { CartDrawer } from './CartDrawer';

interface CartIconProps {
  className?: string;
  showLabel?: boolean;
  labelText?: string;
  /** True when rendered in mobile bottom nav - uses different ID for fly animation */
  isMobile?: boolean;
  /** Icon size: 'md' (default) or 'lg' for floating button */
  iconSize?: 'md' | 'lg';
}

export const CartIcon = memo(function CartIcon({ className = '', showLabel = false, labelText = 'Carrito', isMobile = false, iconSize = 'md' }: CartIconProps) {
  const { cart, isDrawerOpen, openDrawer, closeDrawer } = useCart();
  const prevCountRef = useRef(cart?.totalQuantity || 0);
  const isInitialMount = useRef(true);

  const itemCount = cart?.totalQuantity || 0;

  // Track count changes (for potential future animations)
  useEffect(() => {
    // Skip the first effect run (initial page load)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevCountRef.current = itemCount;
      return;
    }

    if (itemCount > prevCountRef.current) {
      // Count increased - could add animation here in the future
      prevCountRef.current = itemCount;
    } else {
      prevCountRef.current = itemCount;
    }
  }, [itemCount]);

  // Base styles - when showLabel is true, external className handles most styling
  const baseStyles = showLabel 
    ? 'relative transition-colors' 
    : 'relative p-2 text-[#2B2B2B] hover:text-black dark:text-secondary-300 dark:hover:text-secondary-200 transition-colors';

  // Icon size classes
  const iconSizeClass = iconSize === 'lg' ? 'size-7' : 'size-6';
  const badgePositionClass = iconSize === 'lg' ? '-top-1 -right-1' : '-top-1.5 -right-1.5';
  const badgeSizeClass = iconSize === 'lg' ? 'h-5 w-5 text-[11px]' : 'h-4 w-4 text-[10px]';

  return (
    <>
      <button
        id={isMobile ? 'cart-icon-mobile' : 'cart-icon'}
        onClick={openDrawer}
        className={`${baseStyles} ${className}`}
        aria-label={`Carrito (${itemCount} items)`}
      >
        <div className="relative">
          <ShoppingBagIcon className={iconSizeClass} />
          {itemCount > 0 && (
            <span 
              className={`
                absolute ${badgePositionClass} ${badgeSizeClass} flex items-center justify-center 
                bg-black text-white font-bold rounded-full
                transition-all duration-300
                scale-100
              `}
            >
              {itemCount > 99 ? '99+' : itemCount}
            </span>
          )}
        </div>
        {showLabel && (
          <span className="text-xs font-medium">{labelText}</span>
        )}
      </button>

      <CartDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />
    </>
  );
});

